// web/completion.ts — EQUIPMENT COMPLETION: a PURE, DISPLAY-ONLY read-model that frames
// evolution progression as completing each caste's BUILD. For every caste it answers the
// three questions a player should constantly be asking — "how many slots have I filled?",
// "what is the single NEXT step toward completing this caste?", and "which caste should I
// push next?" — so opening boxes reads as PROGRESS TOWARD A BUILD, not collecting random
// stats.
//
// It derives entirely from the web-layer equipment model (web/game.ts `equipmentLayout`:
// occupied / cap / max slots per caste). Like web/colonyPower.ts and web/genePower.ts it
// is render-only:
//   • It NEVER mutates sim state and NEVER draws RNG — every function is a deterministic
//     pure function of its integer inputs.
//   • It is NOT folded into any state hash and is NOT part of the save/replay payload.
//   • It allocates only the small view objects it returns (called on a HUD refresh / in
//     tests, never in the tick hot path).

/** The single next action that advances a caste's build (worst → best). */
export type CompletionStep = 'fill' | 'unlock' | 'complete';

/** The minimal per-caste input this model needs (a subset of game.ts `EquipmentLayout`). */
export interface CompletionInput {
  caste: number;
  name: string;
  filled: number; // occupied slots (genes equipped)
  cap: number;    // currently UNLOCKED slots
  max: number;    // hard maximum slots (GENE_TYPE_COUNT)
}

/** A caste's completion state: counts, the fill fraction, and its single next step. */
export interface CasteCompletion {
  caste: number;
  name: string;
  filled: number;
  cap: number;
  max: number;
  /** Genes needed to fill the currently-unlocked slots (cap − filled, ≥ 0). */
  toFill: number;
  /** Slots still lockable beyond the current cap (max − cap, ≥ 0). */
  toUnlock: number;
  /** Fraction of the CURRENT cap that is filled, 0..1 (1 when cap is 0). Matches the
   *  "filled / cap" headline so the bar and the number always agree. */
  fillPct: number;
  /** Fraction of the HARD MAX that is filled, 0..1 — monotonic "whole build" gauge. */
  totalPct: number;
  step: CompletionStep;
  /** Player-facing next step, e.g. "Need 1 more gene" / "Unlock a slot" / "Complete". */
  hint: string;
}

/** The colony-wide build picture + which caste to focus on next. */
export interface BuildCompletion {
  castes: CasteCompletion[];
  totalFilled: number; // Σ filled
  totalCap: number;    // Σ cap (unlocked slots)
  totalMax: number;    // Σ max (every possible slot)
  /** How many castes are fully built (filled === max, max > 0). */
  complete: number;
  /** The caste to push next (most empty unlocked slots, then most lockable, then lowest
   *  index), or −1 when every caste is fully built. */
  focus: number;
}

/** "1 more gene" / "2 more genes". */
function genesNeeded(n: number): string {
  return `${n} more gene${n === 1 ? '' : 's'}`;
}

/** Derive one caste's completion + its single next step. Pure. */
export function casteCompletion(i: CompletionInput): CasteCompletion {
  const filled = Math.max(0, i.filled);
  const cap = Math.max(0, i.cap);
  const max = Math.max(0, i.max);
  const toFill = Math.max(0, cap - filled);
  const toUnlock = Math.max(0, max - cap);
  const fillPct = cap > 0 ? Math.min(1, filled / cap) : 1;
  const totalPct = max > 0 ? Math.min(1, filled / max) : 1;
  let step: CompletionStep;
  let hint: string;
  if (toFill > 0) {
    step = 'fill';
    hint = `Need ${genesNeeded(toFill)}`;
  } else if (toUnlock > 0) {
    step = 'unlock';
    hint = 'Unlock a slot';
  } else {
    step = 'complete';
    hint = 'Complete';
  }
  return { caste: i.caste, name: i.name, filled, cap, max, toFill, toUnlock, fillPct, totalPct, step, hint };
}

/**
 * Build the colony-wide completion view + the focus caste. Focus priority, in order:
 *   1. the caste with the most EMPTY unlocked slots (toFill), ties → lowest index;
 *   2. else the lowest-index caste that can still UNLOCK a slot (toUnlock > 0);
 *   3. else −1 (every caste fully built).
 * This mirrors the box's own bias (open slots dominate `casteNeedWeights`), so the panel's
 * "focus next" and the box's "favors" point at the same caste in the common case. Pure.
 */
export function buildCompletion(inputs: readonly CompletionInput[]): BuildCompletion {
  const castes = inputs.map(casteCompletion);
  let totalFilled = 0;
  let totalCap = 0;
  let totalMax = 0;
  let complete = 0;
  let focus = -1;
  let bestFill = 0;     // most empty unlocked slots seen
  let unlockFallback = -1;
  for (const c of castes) {
    totalFilled += c.filled;
    totalCap += c.cap;
    totalMax += c.max;
    if (c.max > 0 && c.filled >= c.max) complete++;
    if (c.toFill > bestFill) { bestFill = c.toFill; focus = c.caste; }
    if (unlockFallback < 0 && c.toUnlock > 0) unlockFallback = c.caste;
  }
  if (focus < 0) focus = unlockFallback; // nothing to fill anywhere → first unlockable
  return { castes, totalFilled, totalCap, totalMax, complete, focus };
}
